/**
 * 💰 Patrimonio — vista global de TODOS los activos de Carlos.
 *   • Inmuebles (valor tasación + Airbnb ingresos)
 *   • Cripto (KuCoin live)
 *   • Acciones
 *   • Cash en cuentas bancarias (Cajamar, etc.)
 *   • Deudas (hipoteca pendiente)
 *   • Cash flow mensual
 *
 * NOTA: La calculadora de rentabilidad inmobiliaria se movió a la sección
 * Inmuebles (donde tiene más sentido contextualmente).
 */
import { h, card, apiClient, formatEur, pill, btn, toast, openModal, openConfirm } from './_common.js';

export async function renderPatrimonio(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6 flex-wrap gap-3' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '💰 Patrimonio'),
        h('p', { class: 'text-ink-300' }, 'Tu patrimonio neto en tiempo real.'),
      ]),
      h('div', { class: 'flex gap-2 flex-wrap' }, [
        btn('+ Añadir cuenta/activo', () => openAltAssetModal(null, () => renderPatrimonio(root))),
        btn('Sync KuCoin', async () => {
          try {
            await apiClient.post('/api/v1/portfolio/sync-kucoin', {});
            toast('Sync KuCoin completado', 'success');
            renderPatrimonio(root);
          } catch (e) { toast('Configura KuCoin en Ajustes: ' + (e.message || ''), 'error'); }
        }, { variant: 'secondary' }),
        btn('Refrescar precios', async () => {
          try {
            await apiClient.post('/api/v1/stocks/refresh', {});
            toast('Precios actualizados', 'success');
            renderPatrimonio(root);
          } catch (e) { toast('Error refrescando: ' + (e.message || ''), 'error'); }
        }, { variant: 'secondary' }),
      ]),
    ]),
    h('div', { id: 'pat-quicklinks' }),
    // RESUMEN PATRIMONIO NETO arriba (hero)
    h('div', { id: 'pat-hero', class: 'mb-6' }),
    // KPIs por categoría
    h('div', { id: 'pat-grid', class: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6' }),
    // Detalles
    h('div', { id: 'pat-detail' }),
  ]);
  root.appendChild(wrap);

  // Quick links
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    await renderQuickLinks(wrap.querySelector('#pat-quicklinks'), 'finanzas', {
      title: '🔗 Mis cuentas financieras',
      subtitle: 'KuCoin, Binance, bancos online, brokers — acceso directo.',
      onReload: () => renderPatrimonio(root),
    });
  } catch { /* skip */ }

  // === CARGAR TODOS LOS DATOS EN PARALELO ===
  const [portfolio, debts, income, expenses, goals, propsAgg, earnings, altAssets, propsList] = await Promise.all([
    apiClient.get('/api/v1/portfolio').catch(() => null),
    apiClient.get('/api/v1/debts').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/income?limit=200').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/expenses?limit=200').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/goals').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/properties/aggregate-stats').catch(() => null),
    apiClient.get('/api/v1/airbnb/earnings').catch(() => null),
    apiClient.get('/api/v1/alt-assets').catch(() => ({ data: [], totalValue: 0, byCategory: {} })),
    apiClient.get('/api/v1/properties').catch(() => ({ data: [] })),
  ]);

  // === CÁLCULOS PATRIMONIO ===
  const cryptoEur = portfolio?.crypto?.totalValueEur ?? 0;
  const stocksEur = portfolio?.stocksValueEur ?? 0;
  const totalDebt = debts.data.reduce((s, d) => s + (d.outstandingBalance || d.principalAmount || 0), 0);
  const monthIncome = sumMonth(income.data);
  const monthExp = sumMonth(expenses.data);
  const cashTotal = (altAssets.byCategory?.cash || 0);
  const altOther = (altAssets.totalValue || 0) - cashTotal; // oro, NFTs, etc.

  // Inmuebles: valor tasación del padre + ingresos Airbnb
  const realEstateValue = (propsList.data || [])
    .filter((p) => !p.parentId) // solo "padres" (un piso, no contar estudios x4)
    .reduce((s, p) => s + (p.currentValuation || p.purchasePrice || 0), 0);
  const realEstateMortgage = (propsList.data || []).reduce((s, p) => s + ((p.monthlyMortgage || 0) * 12), 0); // anual estimado
  const airbnbGrossYear = earnings?.thisYear?.gross ?? 0;
  const airbnbGrossTotal = earnings?.totalGross ?? 0;

  // PATRIMONIO NETO total
  const totalActivos = realEstateValue + cryptoEur + stocksEur + cashTotal + altOther;
  const netWorth = totalActivos - totalDebt;

  // === HERO: Patrimonio neto grande arriba ===
  const hero = wrap.querySelector('#pat-hero');
  hero.appendChild(h('div', { class: 'bg-gradient-to-br from-gold-500/10 via-base-900 to-emerald-500/5 border border-gold-400/30 rounded-2xl p-6' }, [
    h('div', { class: 'flex items-baseline justify-between flex-wrap gap-3' }, [
      h('div', {}, [
        h('p', { class: 'text-xs uppercase tracking-wider text-ink-400 font-semibold mb-1' }, 'Patrimonio neto'),
        h('div', { class: 'flex items-baseline gap-3 flex-wrap' }, [
          h('span', { class: 'text-4xl md:text-5xl font-display font-bold text-gold-300' }, formatEur(netWorth)),
          totalDebt > 0 ? h('span', { class: 'text-sm text-ink-400' }, `· activos ${formatEur(totalActivos)} − deudas ${formatEur(totalDebt)}`) : null,
        ]),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-xs text-ink-400' }, 'Cash flow mes'),
        h('div', { class: `text-2xl font-display font-bold ${monthIncome - monthExp >= 0 ? 'text-emerald-300' : 'text-red-300'}` },
          `${monthIncome - monthExp >= 0 ? '+' : ''}${formatEur(monthIncome - monthExp)}`),
      ]),
    ]),
  ]));

  // === KPIs POR CATEGORÍA ===
  const grid = wrap.querySelector('#pat-grid');
  grid.appendChild(stat('🏠 Inmuebles', formatEur(realEstateValue), 'gold', realEstateValue > 0 ? `+${formatEur(airbnbGrossYear)}/año` : 'Sin valor configurado'));
  grid.appendChild(stat('₿ Cripto', formatEur(cryptoEur), 'gold', portfolio?.crypto?.holdings?.length ? `${portfolio.crypto.holdings.length} monedas` : 'KuCoin'));
  grid.appendChild(stat('📈 Acciones', formatEur(stocksEur), 'gold', portfolio?.stocks?.length ? `${portfolio.stocks.length} tickers` : '—'));
  grid.appendChild(stat('💵 Cash bancos', formatEur(cashTotal), 'green', altAssets.data?.filter((a) => a.category === 'cash').length ? `${altAssets.data.filter((a) => a.category === 'cash').length} cuenta(s)` : 'Sin cuentas'));
  if (altOther > 0) grid.appendChild(stat('🪙 Otros activos', formatEur(altOther), 'gold', 'Oro, NFTs, etc.'));
  grid.appendChild(stat('💳 Deudas', formatEur(totalDebt), totalDebt > 0 ? 'red' : 'gray', totalDebt > 0 ? `${debts.data.length} deuda(s)` : 'Sin deudas'));

  // === DETALLES ===
  const detail = wrap.querySelector('#pat-detail');

  // BLOQUE INMUEBLES
  if (realEstateValue > 0 || (propsList.data || []).length > 0) {
    detail.appendChild(buildInmueblesBlock(propsList.data || [], earnings, realEstateValue, airbnbGrossYear, airbnbGrossTotal));
  }

  // BLOQUE CASH / ALT ASSETS
  if (altAssets.data && altAssets.data.length > 0) {
    detail.appendChild(buildAltAssetsBlock(altAssets.data, () => renderPatrimonio(root)));
  }

  // BLOQUE CRIPTO
  if (portfolio?.crypto?.holdings?.length) {
    detail.appendChild(buildHoldings('Holdings Cripto (KuCoin)', portfolio.crypto.holdings));
  }
  if (portfolio?.stocks?.length) {
    detail.appendChild(buildStocks('Acciones', portfolio.stocks));
  }
  detail.appendChild(buildList('Deudas', debts.data, (d) => `${d.name} — ${formatEur(d.outstandingBalance || d.principalAmount)}`));
  detail.appendChild(buildList('Objetivos financieros', goals.data, (g) => `${g.title} — ${formatEur(g.currentValue || 0)} / ${formatEur(g.targetValue || 0)}`));
}

function stat(label, value, color = 'gold', subtext) {
  const colors = { red: 'text-red-300', green: 'text-emerald-300', gold: 'text-gold-300', gray: 'text-ink-300' };
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'text-[10px] uppercase tracking-wider text-ink-400 mb-1' }, label),
    h('div', { class: `text-xl font-display font-bold ${colors[color] || colors.gold} leading-tight` }, value),
    subtext ? h('div', { class: 'text-[10px] text-ink-400 mt-1' }, subtext) : null,
  ]);
}

// =========================================================================
// BLOQUE INMUEBLES — valor + ingresos Airbnb
// =========================================================================
function buildInmueblesBlock(props, earnings, totalValue, grossYear, grossTotal) {
  const c = card('🏠 Activos inmobiliarios', { subtitle: 'Valor de mercado + ingresos Airbnb' });
  const parents = props.filter((p) => !p.parentId);
  if (parents.length === 0) {
    c.appendChild(h('p', { class: 'text-sm text-ink-300' }, 'Sin inmuebles registrados.'));
    return c;
  }

  // KPIs del bloque
  c.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2 mb-4' }, [
    miniKpi('Valor total', formatEur(totalValue), 'gold'),
    miniKpi('Ingresos año', formatEur(grossYear), 'green'),
    miniKpi('Ingresos total', formatEur(grossTotal), 'emerald'),
    miniKpi('Yield bruto', totalValue > 0 ? ((grossYear / totalValue) * 100).toFixed(2) + '%' : '—', 'blue'),
  ]));

  // Lista de inmuebles padre con su valor
  parents.forEach((p) => {
    const valor = p.currentValuation || p.purchasePrice || 0;
    const compra = p.purchasePrice || 0;
    const plusvalia = valor && compra ? valor - compra : 0;
    c.appendChild(h('div', { class: 'flex items-center justify-between py-3 border-b border-base-700/40 last:border-0' }, [
      h('div', {}, [
        h('div', { class: 'font-semibold' }, `🏠 ${p.name}`),
        h('div', { class: 'text-xs text-ink-400' }, [
          compra > 0 ? `Compra: ${formatEur(compra)}` : '',
          plusvalia > 0 ? ` · Plusvalía: ${formatEur(plusvalia)}` : '',
        ].filter(Boolean).join('')),
      ]),
      h('div', { class: 'text-right' }, [
        h('div', { class: 'text-lg font-display font-bold text-gold-300' }, formatEur(valor)),
        h('div', { class: 'text-[10px] text-ink-400' }, 'valor actual'),
      ]),
    ]));
  });

  // Detalle ingresos Airbnb
  if (earnings && earnings.thisYear?.bookings > 0) {
    c.appendChild(h('div', { class: 'mt-4 pt-3 border-t border-base-700/40 text-sm' }, [
      h('span', { class: 'text-ink-300' }, '📊 Airbnb (datos reales): '),
      h('span', { class: 'text-emerald-300 font-semibold' }, `${earnings.thisYear.bookings} reservas año`),
      h('span', { class: 'text-ink-400' }, ' · '),
      h('span', { class: 'text-gold-300' }, `${formatEur(earnings.totalGross)} bruto histórico`),
    ]));
  }

  return c;
}

// =========================================================================
// BLOQUE CASH / ALT ASSETS (cuenta bancaria, oro, NFTs, etc.)
// =========================================================================
function buildAltAssetsBlock(assets, onReload) {
  const c = card('💵 Cuentas bancarias y otros activos');
  const groups = { cash: [], gold: [], collectibles: [], other: [] };
  assets.forEach((a) => {
    const cat = a.category === 'cash' ? 'cash' : a.category === 'gold' ? 'gold' : a.category === 'collectibles' ? 'collectibles' : 'other';
    groups[cat].push(a);
  });

  const labels = { cash: '💵 Cuentas bancarias', gold: '🪙 Oro físico', collectibles: '🎨 Coleccionables', other: '🔸 Otros' };
  Object.entries(groups).forEach(([cat, items]) => {
    if (items.length === 0) return;
    c.appendChild(h('h5', { class: 'font-display font-semibold text-sm mt-3 mb-2 text-ink-200' }, labels[cat]));
    items.forEach((a) => {
      c.appendChild(h('div', { class: 'flex items-center justify-between py-2 border-b border-base-700/40 last:border-0 group' }, [
        h('div', { class: 'flex-1' }, [
          h('div', { class: 'font-medium text-sm' }, a.asset_name),
          h('div', { class: 'text-xs text-ink-400' }, [
            a.platform || '',
            a.platform && a.notes ? ' · ' : '',
            a.notes || '',
          ].filter(Boolean).join('')),
        ]),
        h('div', { class: 'flex items-center gap-2' }, [
          h('div', { class: 'text-lg font-display font-bold text-emerald-300' }, formatEur(a.current_value_eur || 0)),
          h('button', {
            class: 'text-xs text-ink-400 hover:text-gold-300 px-1.5 opacity-0 group-hover:opacity-100',
            onclick: () => openAltAssetModal(a, onReload),
          }, '✎'),
          h('button', {
            class: 'text-xs text-red-400 hover:text-red-300 px-1.5 opacity-0 group-hover:opacity-100',
            onclick: async () => {
              const ok = await openConfirm({ title: 'Eliminar', message: a.asset_name, danger: true });
              if (!ok) return;
              await apiClient.delete(`/api/v1/alt-assets/${a.id}`);
              toast('Eliminado', 'success');
              onReload();
            },
          }, '🗑'),
        ]),
      ]));
    });
  });
  return c;
}

function miniKpi(label, value, color) {
  const colors = {
    gold: 'text-gold-300', green: 'text-emerald-300', emerald: 'text-emerald-400',
    blue: 'text-sky-300', red: 'text-red-300',
  };
  return h('div', { class: 'bg-base-900/40 border border-base-700 rounded-lg p-2 text-center' }, [
    h('div', { class: 'text-[10px] text-ink-400 uppercase tracking-wider' }, label),
    h('div', { class: `text-base font-display font-bold ${colors[color] || colors.gold}` }, value),
  ]);
}

// Modal añadir/editar cuenta bancaria u otro activo
function openAltAssetModal(asset, onDone) {
  openModal({
    title: asset ? 'Editar cuenta/activo' : 'Añadir cuenta bancaria u otro activo',
    icon: '💵',
    size: 'lg',
    fields: [
      { name: 'assetName', label: 'Nombre', value: asset?.asset_name || '', required: true, placeholder: 'p.ej. Cuenta corriente Cajamar' },
      { name: 'category', label: 'Categoría', type: 'select', value: asset?.category || 'cash', options: [
        { value: 'cash', label: '💵 Cuenta bancaria / cash' },
        { value: 'gold', label: '🪙 Oro físico' },
        { value: 'collectibles', label: '🎨 Coleccionables' },
        { value: 'other', label: '🔸 Otro' },
      ] },
      { name: 'platform', label: 'Entidad / plataforma', value: asset?.platform || '', placeholder: 'p.ej. Cajamar, Glint, etc.' },
      { name: 'currentValueEur', label: 'Valor actual (€)', type: 'number', value: asset?.current_value_eur || 0, required: true },
      { name: 'notes', label: 'Notas', type: 'textarea', rows: 2, value: asset?.notes || '' },
    ],
    onSubmit: async (vals) => {
      const payload = {
        assetName: vals.assetName,
        category: vals.category,
        platform: vals.platform || null,
        currentValueEur: Number(vals.currentValueEur) || 0,
        quantity: Number(vals.currentValueEur) || 0,
        unit: 'EUR',
        notes: vals.notes || null,
      };
      if (asset?.id) {
        await apiClient.patch(`/api/v1/alt-assets/${asset.id}`, payload);
        toast('Actualizado', 'success');
      } else {
        await apiClient.post('/api/v1/alt-assets', payload);
        toast('Añadido', 'success');
      }
      onDone();
    },
  });
}

function buildHoldings(title, holdings) {
  const c = card(title);
  holdings.slice(0, 10).forEach((h2) => c.appendChild(
    h('div', { class: 'flex items-center justify-between text-sm py-1.5 border-b border-base-700 last:border-0' }, [
      h('span', { class: 'font-mono' }, h2.symbol),
      h('span', { class: 'text-ink-300' }, h2.qty.toFixed(4)),
      h('span', { class: 'text-gold-300' }, formatEur(h2.valueEur)),
    ]),
  ));
  return c;
}

function buildStocks(title, stocks) {
  const c = card(title);
  stocks.forEach((s) => c.appendChild(
    h('div', { class: 'flex items-center justify-between text-sm py-1.5 border-b border-base-700 last:border-0' }, [
      h('span', { class: 'font-mono' }, s.ticker),
      h('span', { class: 'text-ink-300' }, `${s.shares} @ ${s.avgPrice}`),
      h('span', { class: 'text-gold-300' }, formatEur(s.currentValueEur || s.shares * s.avgPrice)),
    ]),
  ));
  return c;
}

function buildList(title, items, fmt) {
  const c = card(title);
  if (items.length === 0) {
    c.appendChild(h('p', { class: 'text-sm text-ink-300' }, 'Sin elementos.'));
    return c;
  }
  items.forEach((it) => c.appendChild(h('div', { class: 'py-2 border-b border-base-700 last:border-0 text-sm' }, fmt(it))));
  return c;
}

function sumMonth(items) {
  const now = new Date();
  return items.filter((i) => {
    const d = new Date(i.occurredAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((s, i) => s + i.amount, 0);
}
